import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import "./SwaggerEntityRenderer.css";

/**
 * SwaggerEntityRenderer - A reusable component for rendering Swagger/OpenAPI entity definitions
 * Displays schema definitions with their properties in a structured, expandable format
 */
const SwaggerEntityRenderer = ({
  entity,
  entityName,
  definitions = {},
  showDescription = true,
  expandedByDefault = {},
  showTitle = true,
}) => {
  const [expandedSchemas, setExpandedSchemas] = useState(expandedByDefault);

  // Helper function to toggle schema expansion
  const toggleSchemaExpansion = (schemaKey) => {
    setExpandedSchemas((prev) => ({
      ...prev,
      [schemaKey]: !prev[schemaKey],
    }));
  };

  // Helper function to get type display name
  const getTypeDisplayName = (schema) => {
    if (!schema) return "any";

    if (schema.$ref) {
      return schema.$ref.split("/").pop();
    }

    if (schema.type === "array") {
      if (schema.items?.$ref) {
        return `Array of ${schema.items.$ref.split("/").pop()}`;
      }
      return `Array of ${schema.items?.type || "any"}`;
    }

    if (schema.enum) {
      return "enum";
    }

    return schema.type || "object";
  };

  // Helper function to render schema properties recursively
  const renderSchemaProperties = (
    schema,
    schemaName,
    parentKey = "",
    level = 0
  ) => {
    const currentKey = parentKey ? `${parentKey}.${schemaName}` : schemaName;

    if (!schema) return null;

    // Handle references
    if (schema.$ref) {
      const refName = schema.$ref.split("/").pop();
      const refSchema = definitions[refName];

      if (!refSchema) {
        return (
          <div className="schema-ref-missing">
            Reference to undefined schema: {refName}
          </div>
        );
      }

      return renderSchemaProperties(refSchema, refName, parentKey, level);
    }

    // Handle enums
    if (schema.enum) {
      return (
        <div className="schema-enum-container">
          <div className="schema-enum-type">
            <span className="enum-label">Enum values:</span>
            <span className="enum-values">{schema.enum.join(", ")}</span>
          </div>
          {schema.description && (
            <div className="enum-description">{schema.description}</div>
          )}
        </div>
      );
    }

    // Handle arrays
    if (schema.type === "array" && schema.items) {
      return (
        <div className="schema-array-container">
          <div className="array-items-label">Array items:</div>
          <div className="array-items-content">
            {renderSchemaProperties(
              schema.items,
              "items",
              currentKey,
              level + 1
            )}
          </div>
        </div>
      );
    }

    // Handle objects with properties
    if (schema.properties) {
      return (
        <div className="schema-properties-container">
          {Object.entries(schema.properties).map(([propName, propSchema]) => {
            const propKey = `${currentKey}.${propName}`;
            const isExpandable =
              propSchema.$ref ||
              propSchema.type === "object" ||
              propSchema.type === "array" ||
              (propSchema.properties &&
                Object.keys(propSchema.properties).length > 0);
            const isPropExpanded = expandedSchemas[propKey];

            // Get type information
            const typeName = getTypeDisplayName(propSchema);

            // Check for enum values
            let enumValues = null;
            if (propSchema.$ref) {
              const refName = propSchema.$ref.split("/").pop();
              const refSchema = definitions[refName];
              if (refSchema?.enum) {
                enumValues = refSchema.enum;
              }
            } else if (propSchema.enum) {
              enumValues = propSchema.enum;
            }

            return (
              <div key={propName} className="property-container">
                <div className="property-header">
                  <div className="property-name-section">
                    {isExpandable && (
                      <span
                        className="expand-arrow"
                        onClick={() => toggleSchemaExpansion(propKey)}
                      >
                        {isPropExpanded ? "▼" : "▶"}
                      </span>
                    )}
                    <span
                      className={`property-name ${
                        isExpandable ? "expandable" : ""
                      }`}
                      onClick={
                        isExpandable
                          ? () => toggleSchemaExpansion(propKey)
                          : undefined
                      }
                    >
                      {propName}
                    </span>
                  </div>
                  <div className="property-info">
                    <div className="property-type">
                      {typeName}
                      {propSchema.format && ` (${propSchema.format})`}
                    </div>
                    <div
                      className={
                        schema.required?.includes(propName)
                          ? "required-badge"
                          : "optional-badge"
                      }
                    >
                      {schema.required?.includes(propName)
                        ? "required"
                        : "optional"}
                    </div>
                  </div>
                </div>

                {propSchema.description && (
                  <div className="property-description">
                    {propSchema.description}
                  </div>
                )}

                {enumValues && (
                  <div className="property-enum">
                    <span className="enum-label">Possible values:</span>
                    <span className="enum-list">{enumValues.join(", ")}</span>
                  </div>
                )}

                {propSchema.example !== undefined && (
                  <div className="property-example">
                    <span className="example-label">Example:</span>
                    <span className="example-value">
                      {JSON.stringify(propSchema.example)}
                    </span>
                  </div>
                )}

                {propSchema.default !== undefined && (
                  <div className="property-default">
                    <span className="default-label">Default:</span>
                    <span className="default-value">
                      {JSON.stringify(propSchema.default)}
                    </span>
                  </div>
                )}

                {isExpandable && isPropExpanded && (
                  <div className="nested-content">
                    {renderSchemaProperties(
                      propSchema,
                      propName,
                      currentKey,
                      level + 1
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Handle primitive types
    return (
      <div className="schema-primitive">
        <div className="primitive-type">
          Type: {schema.type || "any"}
          {schema.format && ` (${schema.format})`}
        </div>
        {schema.description && (
          <div className="primitive-description">{schema.description}</div>
        )}
        {schema.example !== undefined && (
          <div className="primitive-example">
            Example: {JSON.stringify(schema.example)}
          </div>
        )}
        {schema.default !== undefined && (
          <div className="primitive-default">
            Default: {JSON.stringify(schema.default)}
          </div>
        )}
        {schema.pattern && (
          <div className="primitive-pattern">Pattern: {schema.pattern}</div>
        )}
        {schema.minimum !== undefined && (
          <div className="primitive-constraint">Minimum: {schema.minimum}</div>
        )}
        {schema.maximum !== undefined && (
          <div className="primitive-constraint">Maximum: {schema.maximum}</div>
        )}
        {schema.minLength !== undefined && (
          <div className="primitive-constraint">
            Min Length: {schema.minLength}
          </div>
        )}
        {schema.maxLength !== undefined && (
          <div className="primitive-constraint">
            Max Length: {schema.maxLength}
          </div>
        )}
      </div>
    );
  };

  if (!entity) {
    return (
      <div className="swagger-entity-renderer-error">
        No entity data provided
      </div>
    );
  }

  return (
    <div className="swagger-entity-renderer">
      {/* Entity Header */}
      {showTitle && (
        <div className="entity-header">
          <h3 className="entity-name">{entityName || "Entity"}</h3>
          {entity.type && (
            <span className="entity-type-badge">{entity.type}</span>
          )}
        </div>
      )}

      {/* Entity Description */}
      {showDescription && entity.description && (
        <div className="entity-description">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {entity.description}
          </ReactMarkdown>
        </div>
      )}

      {/* Entity Properties */}
      <div className="entity-content">
        {renderSchemaProperties(entity, entityName || "entity")}
      </div>

      {/* Additional Information */}
      {(entity.example || entity.examples) && (
        <div className="entity-examples">
          <h4 className="examples-title">Examples</h4>
          <div className="examples-content">
            {entity.example && (
              <pre className="example-code">
                {JSON.stringify(entity.example, null, 2)}
              </pre>
            )}
            {entity.examples &&
              Object.entries(entity.examples).map(([name, example]) => (
                <div key={name} className="named-example">
                  <div className="example-name">{name}</div>
                  <pre className="example-code">
                    {JSON.stringify(example.value || example, null, 2)}
                  </pre>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Discriminator Information */}
      {entity.discriminator && (
        <div className="entity-discriminator">
          <h4 className="discriminator-title">Discriminator</h4>
          <div className="discriminator-content">
            <div className="discriminator-property">
              Property:{" "}
              <span className="discriminator-value">
                {entity.discriminator.propertyName || entity.discriminator}
              </span>
            </div>
            {entity.discriminator.mapping && (
              <div className="discriminator-mapping">
                <div className="mapping-title">Mappings:</div>
                {Object.entries(entity.discriminator.mapping).map(
                  ([key, value]) => (
                    <div key={key} className="mapping-item">
                      <span className="mapping-key">{key}</span>
                      <span className="mapping-arrow">→</span>
                      <span className="mapping-value">{value}</span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* XML Information */}
      {entity.xml && (
        <div className="entity-xml">
          <h4 className="xml-title">XML</h4>
          <div className="xml-content">
            {entity.xml.name && (
              <div className="xml-property">
                Name: <span className="xml-value">{entity.xml.name}</span>
              </div>
            )}
            {entity.xml.namespace && (
              <div className="xml-property">
                Namespace:{" "}
                <span className="xml-value">{entity.xml.namespace}</span>
              </div>
            )}
            {entity.xml.prefix && (
              <div className="xml-property">
                Prefix: <span className="xml-value">{entity.xml.prefix}</span>
              </div>
            )}
            {entity.xml.wrapped !== undefined && (
              <div className="xml-property">
                Wrapped:{" "}
                <span className="xml-value">
                  {entity.xml.wrapped.toString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

SwaggerEntityRenderer.propTypes = {
  entity: PropTypes.object.isRequired,
  entityName: PropTypes.string,
  definitions: PropTypes.object,
  showDescription: PropTypes.bool,
  expandedByDefault: PropTypes.object,
  showTitle: PropTypes.bool,
};

export default SwaggerEntityRenderer;
